import React from 'react';
import { GradientTextAction } from 'src/common';
import styled from 'styled-components';

const LogoUploadDiv = styled.div`
  .logo-current {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;

    &__details {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &__image {
      width: 180px;
      height: 64px;
      object-fit: contain;
      background-color: ${({ theme }) => theme.colors.white};
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      border-radius: 6px;
      padding: 6px 10px;
    }

    &__placeholder {
      width: 200px;
      height: 107px;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: ${({ theme }) => theme.colors.cultured};
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__placeholder-icon {
      width: 28px;
      height: 31px;
      color: #6a7590;
    }
  }

  .logo-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .logo-current {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

interface LogoUploadProps {
  displayLogoUrl: string;
  hasOrganisationLogo: boolean;
  hasLogoPreview: boolean;
  currentLogoShadow: boolean;
  canEditBranding: boolean;
  canRemoveLogo: boolean;
  brandingEnabled: boolean;
  isRemovingLogo: boolean;
  logoInputRef: React.RefObject<HTMLInputElement>;
  onPickLogo: () => void;
  onRemoveLogo: () => void;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  children?: React.ReactNode;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  displayLogoUrl,
  hasOrganisationLogo,
  hasLogoPreview,
  currentLogoShadow,
  canEditBranding,
  canRemoveLogo,
  brandingEnabled,
  isRemovingLogo,
  logoInputRef,
  onPickLogo,
  onRemoveLogo,
  onFileChange,
  children,
}) => {
  return (
    <LogoUploadDiv>
      <div className="logo-current">
        <div className="logo-current__details">
          {hasOrganisationLogo || hasLogoPreview ? (
            <img
              className={['logo-current__image', currentLogoShadow && 'logo-shadow'].filter(Boolean).join(' ')}
              src={displayLogoUrl}
              alt="Current organisation logo"
            />
          ) : (
            <div className="logo-current__placeholder" aria-hidden>
              <svg
                className="logo-current__placeholder-icon"
                viewBox="0 0 28 31"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M26.3984 29.0625H25.1875V1.45312C25.1875 0.650576 24.5369 0 23.7344 0H3.39062C2.58808 0 1.9375 0.650576 1.9375 1.45312V29.0625H0.726562C0.325318 29.0625 0 29.3878 0 29.7891V31H27.125V29.7891C27.125 29.3878 26.7997 29.0625 26.3984 29.0625ZM7.75 4.60156C7.75 4.20032 8.07532 3.875 8.47656 3.875H10.8984C11.2997 3.875 11.625 4.20032 11.625 4.60156V7.02344C11.625 7.42468 11.2997 7.75 10.8984 7.75H8.47656C8.07532 7.75 7.75 7.42468 7.75 7.02344V4.60156ZM7.75 10.4141C7.75 10.0128 8.07532 9.6875 8.47656 9.6875H10.8984C11.2997 9.6875 11.625 10.0128 11.625 10.4141V12.8359C11.625 13.2372 11.2997 13.5625 10.8984 13.5625H8.47656C8.07532 13.5625 7.75 13.2372 7.75 12.8359V10.4141ZM10.8984 19.375H8.47656C8.07532 19.375 7.75 19.0497 7.75 18.6484V16.2266C7.75 15.8253 8.07532 15.5 8.47656 15.5H10.8984C11.2997 15.5 11.625 15.8253 11.625 16.2266V18.6484C11.625 19.0497 11.2997 19.375 10.8984 19.375ZM15.5 29.0625H11.625V23.9766C11.625 23.5753 11.9503 23.25 12.3516 23.25H14.7734C15.1747 23.25 15.5 23.5753 15.5 23.9766V29.0625ZM19.375 18.6484C19.375 19.0497 19.0497 19.375 18.6484 19.375H16.2266C15.8253 19.375 15.5 19.0497 15.5 18.6484V16.2266C15.5 15.8253 15.8253 15.5 16.2266 15.5H18.6484C19.0497 15.5 19.375 15.8253 19.375 16.2266V18.6484ZM19.375 12.8359C19.375 13.2372 19.0497 13.5625 18.6484 13.5625H16.2266C15.8253 13.5625 15.5 13.2372 15.5 12.8359V10.4141C15.5 10.0128 15.8253 9.6875 16.2266 9.6875H18.6484C19.0497 9.6875 19.375 10.0128 19.375 10.4141V12.8359ZM19.375 7.02344C19.375 7.42468 19.0497 7.75 18.6484 7.75H16.2266C15.8253 7.75 15.5 7.42468 15.5 7.02344V4.60156C15.5 4.20032 15.8253 3.875 16.2266 3.875H18.6484C19.0497 3.875 19.375 4.20032 19.375 4.60156V7.02344Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          )}
          {brandingEnabled && (
            <div className="logo-actions">
              <GradientTextAction
                $underline
                $disabled={!canEditBranding}
                px={14}
                weight={500}
                onClick={onPickLogo}
              >
                {hasOrganisationLogo ? 'Update' : 'Upload logo'}
              </GradientTextAction>
              {hasOrganisationLogo && (
                <GradientTextAction
                  $underline
                  $disabled={!canRemoveLogo}
                  px={14}
                  weight={500}
                  onClick={onRemoveLogo}
                >
                  Remove
                </GradientTextAction>
              )}
            </div>
          )}
        </div>
        <input
          ref={logoInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          style={{ display: 'none' }}
          onChange={onFileChange}
        />
        {children}
      </div>
    </LogoUploadDiv>
  );
};
